import { Checkbox, Dialog } from '@eppendorf/vnls-react-components';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  ConfirmationDialog,
  ConfirmDialogIconType,
} from '$components/confirmation-dialog/confirmation-dialog';
import { DeviceNameCell } from '$components/device/name-cell';
import { RouterStatus, RouterStatusBadge } from '$components/device/router-status-badge';
import { SignalStrengthIcon } from '$components/device/signal-strength-icon';
import {
  AssociatedDevicesList,
  AssociatedDevicesCard,
} from '$features/devices/associated-devices/associated-devices-list/associated-devices-list';
import styles from '$features/devices/associated-devices/associated-devices-list/associated-devices-list.module.scss';
import {
  createDeviceQuery,
  useGetDevice,
  useGetGatewayAssociationList,
  useGetSenseDevice,
  useUpdateMonitorBlockedState,
} from '$features/devices/devices.api';
import { AssociatedMonitorModel } from '$features/devices/devices.types';

export function AssociatedMonitors() {
  const [searchParams] = useSearchParams();
  const gatewayId = searchParams.get('serialNumber') ?? '';
  const { t } = useTranslation();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [selectedAssociatedMonitor, setSelectedAssociatedMonitor] = useState({
    serialNumber: '',
    name: '',
    isBlocked: false,
  });

  const {
    isPending: isPatchBlockListLoading,
    mutateAsync: patchBlockList,
    isSuccess: isBlockListPatchSuccess,
    variables: blockListPatchVariables,
  } = useUpdateMonitorBlockedState(gatewayId);

  const associationList = useGetGatewayAssociationList(gatewayId);

  const { data: inventoryGateway } = useGetDevice({
    manufacturer: searchParams.get('manufacturer') ?? '',
    serialNumber: gatewayId,
  });

  const {
    data: senseGateway = { serialNumber: '', clientsAvailable: [] },
    isLoading: isSenseDeviceLoading,
  } = useGetSenseDevice(gatewayId, 'gateways');

  const queryResults = useQueries({
    queries: senseGateway.clientsAvailable.map((client) =>
      createDeviceQuery({
        manufacturer: searchParams.get('manufacturer') ?? '',
        serialNumber: client.clientId,
        enabled: !isSenseDeviceLoading,
      }),
    ),
  });

  const allQueriesCompleted =
    !isSenseDeviceLoading &&
    queryResults.length === senseGateway.clientsAvailable.length &&
    queryResults.every((result) => result.isSuccess || result.isError) &&
    (associationList.isSuccess || associationList.isError);

  useEffect(() => {
    if (!isPatchBlockListLoading && isBlockListPatchSuccess) {
      setIsConfirmationDialogOpen(true);
    }
  }, [isPatchBlockListLoading]);

  const columnsData = useMemo(
    () =>
      senseGateway.clientsAvailable.map((availableClient, index) => {
        const availableClientInventory = queryResults.at(index)?.data;

        // optimistic update of last block/unblock mutation
        const isBlocked =
          blockListPatchVariables?.monitorId === availableClient.clientId
            ? blockListPatchVariables.isBlocked
            : associationList.data?.blocked.includes(availableClient.clientId);

        const monitor: AssociatedMonitorModel = {
          serialNumber: availableClient.clientId || '',
          manufacturer: availableClientInventory?.manufacturer ?? 'Eppendorf',
          model:
            availableClientInventory?.model ?? 'VisioNize sense environmental monitor',
          monitorConnectionStatus:
            availableClient.registrationState === 'notWhitelisted'
              ? RouterStatus.BLOCKED
              : availableClient.connectionStatus,
          signalStrength: availableClient.signalStrength,
          name: availableClientInventory?.name ?? availableClient.clientId,
          type: availableClientInventory?.type ?? 'sense-monitor',
          isBlocked,
        };
        return monitor;
      }),
    [
      queryResults,
      senseGateway.clientsAvailable,
      associationList.data,
      blockListPatchVariables,
      isPatchBlockListLoading,
    ],
  );

  const handleOnBlock = async (
    serialNumber: string,
    associatedMonitorName: string,
    isBlocked: boolean,
  ) => {
    setSelectedAssociatedMonitor({
      serialNumber,
      name: associatedMonitorName,
      isBlocked,
    });
    await patchBlockList({
      monitorId: serialNumber,
      isBlocked,
    });
  };

  return (
    <>
      <AssociatedDevicesList
        loading={isSenseDeviceLoading || !allQueriesCompleted}
        showEmptyState={
          !isSenseDeviceLoading && allQueriesCompleted && columnsData.length === 0
        }
        emptyMessage={t('deviceDetail.monitorsTable.emptyMessage')}
      >
        {columnsData.map((device) => (
          <AssociatedDevicesCard
            key={device.serialNumber}
            className={styles.associatedDeviceCardWithTwoRows}
          >
            <AssociatedDevicesCard.Status>
              <RouterStatusBadge status={device.monitorConnectionStatus} size="medium" />
              <SignalStrengthIcon signalStrength={device.signalStrength} />
            </AssociatedDevicesCard.Status>
            <AssociatedDevicesCard.Content>
              <DeviceNameCell device={device} />
            </AssociatedDevicesCard.Content>
            <AssociatedDevicesCard.Actions>
              <Checkbox
                checked={device.isBlocked}
                onChange={(checked) =>
                  handleOnBlock(device.serialNumber, device.name, Boolean(checked))
                }
              />
            </AssociatedDevicesCard.Actions>
          </AssociatedDevicesCard>
        ))}
      </AssociatedDevicesList>

      <Dialog
        isOpen={isConfirmationDialogOpen}
        onOpenChange={(state) => setIsConfirmationDialogOpen(state)}
        hideCloseButton
      >
        <div title="associated-monitors-confirmation-dialog">
          <ConfirmationDialog
            title={t(
              `deviceDetail.tabs.associatedMonitorsDialog.${selectedAssociatedMonitor.isBlocked ? 'blockedTitle' : 'unblockedTitle'}`,
            )}
            dialogDescription={
              <>
                <Trans
                  i18nKey={`deviceDetail.tabs.associatedMonitorsDialog.${selectedAssociatedMonitor.isBlocked ? 'blockedDescription' : 'unblockedDescription'}`}
                  values={{
                    gatewayName: inventoryGateway?.name ?? 'Device',
                    associatedMonitorName: selectedAssociatedMonitor.name,
                  }}
                  components={{ bold: <span className="font-weight-bold" /> }}
                />
                <span style={{ marginTop: 8, fontStyle: 'italic', display: 'block' }}>
                  {t('deviceDetail.tabs.associatedMonitorsDialog.disclaimer')}
                </span>
              </>
            }
            iconType={ConfirmDialogIconType.success}
          />
        </div>
      </Dialog>
    </>
  );
}
